/**
 * Company revenue display (FPE-9613).
 *
 * `curr_financial_info.revenue_norm` is a normalized RANGE that the ingestion
 * pipeline derives from the `est_revenue` label. It is not a revenue value:
 *
 *   "211915000000"   -> { from: 211915000000, to: 211915000000 }  exact
 *   "1M-5M"          -> { from: 1000000,      to: 5000000 }       bucket
 *   "Less than $50M" -> { from: 0,            to: 50000000 }      bucket
 *   "More than $1B"  -> { from: 1000000000,   to: 1e15 }          bucket
 *
 * So rendering `revenue_norm.from` shows 0 for every "less than" bucket and
 * the lower bound for every other bucket. Pushing `est_revenue` through
 * parseInt is equally wrong: parseInt("1M-5M") === 1.
 *
 * Resolve the value here; each call site formats it with its own humanizer.
 */

export interface ICompanyRevenueSource {
  est_revenue?: string | number | null;
  revenue_norm?: { from?: number | null; to?: number | null } | null;
}

export type TCompanyRevenue =
  /** An exact figure. Format with the call site's humanizer. */
  | { kind: 'exact'; value: number }
  /** A bucket label such as "1M-5M". Render verbatim - never parseInt it. */
  | { kind: 'label'; value: string }
  /** No revenue data. The call site hides the row or shows its placeholder. */
  | { kind: 'none' };

/** Turns a number into the string a call site wants to display. */
export type TRevenueHumanizer = (value: number) => string | number | undefined;

export const resolveCompanyRevenue = ({
  financialInfo
}: {
  financialInfo: ICompanyRevenueSource | null | undefined;
}): TCompanyRevenue => {
  const norm = financialInfo?.revenue_norm;

  // An exact figure normalizes to from === to, so the bound is the value.
  if (norm?.from != undefined && norm.from === norm.to) {
    return { kind: 'exact', value: norm.from };
  }

  const est = financialInfo?.est_revenue;

  if (est == undefined) {
    return { kind: 'none' };
  }

  const text = String(est).trim();

  if (text.length === 0) {
    return { kind: 'none' };
  }

  // est_revenue can still hold an exact figure when revenue_norm is missing.
  // Only treat it as a number when the whole string parses.
  const asNumber = Number(text);

  if (!Number.isNaN(asNumber)) {
    return { kind: 'exact', value: asNumber };
  }

  return { kind: 'label', value: text };
};

/** The common "humanize the figure, or hide the row" render shape. */
export const formatCompanyRevenue = ({
  financialInfo,
  humanize
}: {
  financialInfo: ICompanyRevenueSource | null | undefined;
  humanize: TRevenueHumanizer;
}): string | undefined => {
  const revenue = resolveCompanyRevenue({ financialInfo });

  if (revenue.kind === 'exact') {
    const humanized = humanize(revenue.value);

    return humanized == undefined ? undefined : String(humanized);
  }

  if (revenue.kind === 'label') {
    return revenue.value;
  }

  return undefined;
};

/**
 * For a call site that holds only an `est_revenue` value and no
 * `revenue_norm` - a single financial-info row, or a source that reports the
 * label directly. Same rules: humanize a figure, pass a label through.
 */
export const formatRevenueLabel = ({
  estRevenue,
  humanize
}: {
  estRevenue: ICompanyRevenueSource['est_revenue'];
  humanize: TRevenueHumanizer;
}): string | undefined =>
  formatCompanyRevenue({ financialInfo: { est_revenue: estRevenue }, humanize });

/**
 * The same rule, as a number for ordering only - never displayed.
 * Mirrors resolveCompanyRevenue: from === to is the exact figure, otherwise
 * it is a bucket and `to` is the figure its label states.
 */
export const resolveCompanyRevenueSortValue = ({
  financialInfo
}: {
  financialInfo: ICompanyRevenueSource | null | undefined;
}): number | null => {
  const norm = financialInfo?.revenue_norm;

  if (norm?.from == undefined) {
    return null;
  }

  return norm.from === norm.to ? norm.from : norm.to ?? null;
};
